/*
 * Cart drawer: open/close, quantity changes, remove, live refresh.
 * Exposes window.AnishCartDrawer so theme.js can open it after add-to-cart.
 */
(() => {
  const SECTION_ID = 'cart-drawer';
  const root = window.Shopify?.routes?.root || '/';
  const routes = {
    change: `${root}cart/change.js`,
    section: `${root}?sections=${SECTION_ID}`,
  };

  const getDrawer = () => document.querySelector('[data-cart-drawer]');
  let lastFocused = null;

  const isEnabled = () => getDrawer()?.dataset.enabled === 'true';
  const opensOnAdd = () => isEnabled() && getDrawer()?.dataset.openOnAdd === 'true';

  const updateBubbles = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((bubble) => {
      bubble.textContent = String(count);
      bubble.classList.toggle('is-empty', count === 0);
      bubble.classList.add('is-updated');
      window.setTimeout(() => bubble.classList.remove('is-updated'), 260);
    });
  };

  // Swap only the inner content so open/closed state and focus handling survive.
  const renderSection = (html) => {
    const drawer = getDrawer();
    if (!drawer || !html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-drawer-inner]');
    const current = drawer.querySelector('[data-cart-drawer-inner]');
    if (fresh && current) current.replaceWith(fresh);
  };

  const refresh = async () => {
    const response = await fetch(routes.section, { headers: { Accept: 'application/json' } });
    const sections = await response.json();
    renderSection(sections[SECTION_ID]);
  };

  const showError = (message) => {
    const box = getDrawer()?.querySelector('[data-cart-drawer-error]');
    if (!box) return;
    box.textContent = message;
    box.hidden = false;
    window.clearTimeout(box._timer);
    box._timer = window.setTimeout(() => { box.hidden = true; }, 5000);
  };

  const focusables = (el) =>
    Array.from(el.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      .filter((node) => node.offsetParent !== null);

  const open = () => {
    const drawer = getDrawer();
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    // Close the mobile menu if it is open.
    document.querySelector('.nav-links.is-open')?.classList.remove('is-open');
    document.querySelector('.menu-toggle')?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('menu-open');
    window.setTimeout(() => drawer.querySelector('.cart-drawer__close')?.focus(), 50);
  };

  const close = () => {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  };

  const changeQuantity = async (key, quantity) => {
    const drawer = getDrawer();
    drawer?.classList.add('is-loading');
    try {
      const response = await fetch(routes.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: [SECTION_ID], sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to update cart.');
      if (data.sections?.[SECTION_ID]) renderSection(data.sections[SECTION_ID]);
      else await refresh();
      updateBubbles(data.item_count || 0);
    } catch (error) {
      await refresh().catch(() => {});
      showError(error.message);
    } finally {
      drawer?.classList.remove('is-loading');
    }
  };

  // Called by theme.js after a successful add-to-cart.
  const handleAdded = async (cart) => {
    if (!opensOnAdd()) return false;
    try {
      await refresh();
    } catch (error) {
      return false;
    }
    if (cart) updateBubbles(cart.item_count || 0);
    open();
    return true;
  };

  document.addEventListener('click', (event) => {
    const drawer = getDrawer();

    const cartLink = event.target.closest('.cart-icon-link');
    if (cartLink && isEnabled() && !/\/cart\/?$/.test(window.location.pathname)) {
      event.preventDefault();
      open();
      return;
    }

    if (!drawer || !drawer.contains(event.target)) return;

    if (event.target.closest('[data-cart-drawer-close]')) {
      close();
      return;
    }

    const qtyButton = event.target.closest('[data-cart-qty]');
    if (qtyButton && !qtyButton.disabled) {
      const item = qtyButton.closest('[data-cart-item]');
      if (item) changeQuantity(item.dataset.key, Math.max(0, Number(qtyButton.dataset.cartQty)));
    }
  });

  document.addEventListener('keydown', (event) => {
    const drawer = getDrawer();
    if (!drawer?.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key === 'Tab') {
      const nodes = focusables(drawer.querySelector('.cart-drawer__panel'));
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  // Keep the drawer in sync when the page is restored from the back/forward cache.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && getDrawer()) refresh().catch(() => {});
  });

  window.AnishCartDrawer = { open, close, refresh, handleAdded, isEnabled, opensOnAdd };
})();
